import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Tab from "@mui/material/Tab";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { ApiError } from "../../api/errors";
import {
  getAnkhInvoice,
  getAnkhInvoicePdf,
  getAnkhReceiptPdf,
  listAnkhInvoices,
  listAnkhReceipts,
  type AnkhInvoice,
  type AnkhReceipt,
} from "../../api/ankhInvoices";

const inr = (n: number) => n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** An instant as its calendar date in India, dd-mm-yyyy. */
export function istDate(s: string): string {
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Kolkata", day: "2-digit", month: "2-digit", year: "numeric" }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  return `${get("day")}-${get("month")}-${get("year")}`;
}

/** "O/2026-27/00001" → "O-2026-27-00001.pdf": a slash is not allowed in a file name. */
export function pdfFileName(inv: Pick<AnkhInvoice, "invoicenumber" | "id">): string {
  return `${(inv.invoicenumber || inv.id).replace(/[^A-Za-z0-9._-]+/g, "-")}.pdf`;
}

/** Hand a fetched PDF to the browser as a download. */
export function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

function errorTitle(e: Error, what: string): string {
  if (e instanceof ApiError) {
    // radbilling: a Draft has no number and no document yet.
    if (e.status === 409) return "This invoice is not issued yet, so it has no PDF";
    switch (e.kind) {
      case "fail-closed":
        return `Nothing was sent for ${what}`;
      case "unauthenticated":
        return "Your sign-in has expired -- sign in again";
      case "forbidden":
        return "Refused -- your role cannot read what Ankh billed this provider";
      case "not-found":
        return `Not found: ${what}`;
      case "at-capacity":
        return "The service is busy -- try again shortly";
      case "bad-request":
        return `The request for ${what} was refused`;
      case "unavailable":
        return `Could not reach the service for ${what}`;
    }
  }
  return `Could not complete ${what}`;
}

function ErrorAlert({ error, what }: { error: Error; what: string }) {
  return (
    <Alert severity="error" sx={{ mb: 2 }}>
      <AlertTitle>{errorTitle(error, what)}</AlertTitle>
      {error.message}
    </Alert>
  );
}

function StatusChip({ status }: { status: string }) {
  const color = status === "Paid" ? "success" : status === "Draft" ? "default" : "warning";
  return <Chip size="small" label={status || "unknown"} color={color} variant={status === "Paid" ? "filled" : "outlined"} />;
}

/**
 * Billing › Invoices from Ankh: what AnkhOS billed the provider the console is
 * working in -- its subscription to AnkhOS, cloud hosting and the like.
 *
 * 🔑 Not the provider's own sales. The invoices a provider issues to ITS
 * customers are its tenant ledger, reached from Record payment. Two ledgers,
 * two screens, and the title says which one this is.
 */
export function AnkhInvoicesScreen() {
  const identity = useIdentity();
  const ns = identity.namespace ?? "";
  const [tab, setTab] = useState<"invoices" | "receipts">("invoices");

  return (
    <Box sx={{ maxWidth: 1100 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Invoices from Ankh
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        What AnkhOS billed <code>{ns || "no namespace"}</code>, and the receipts for what it paid. Invoices this provider
        issues to its own customers are not listed here; they are under Billing › Record payment.
      </Typography>
      <Tabs value={tab} onChange={(_, v: "invoices" | "receipts") => setTab(v)} sx={{ mb: 2 }}>
        <Tab value="invoices" label="Invoices" />
        <Tab value="receipts" label="Receipts" />
      </Tabs>
      {tab === "invoices" ? <InvoiceList ns={ns} /> : <ReceiptList ns={ns} />}
    </Box>
  );
}

function InvoiceList({ ns }: { ns: string }) {
  const identity = useIdentity();
  const [openId, setOpenId] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ["ankh-invoices", ns],
    queryFn: ({ signal }) => listAnkhInvoices(identity, ns, signal),
    enabled: Boolean(identity.token && ns),
    retry: false,
  });

  return (
    <Box>
      {list.isLoading && <Typography variant="body2">Loading…</Typography>}
      {list.isError && <ErrorAlert error={list.error as Error} what="the invoice list" />}

      {list.data && list.data.length === 0 && (
        <Alert severity="info">
          <AlertTitle>No invoices from Ankh</AlertTitle>
          AnkhOS has not billed <code>{ns}</code>.
        </Alert>
      )}

      {list.data && list.data.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small" aria-label="Invoices from Ankh">
            <TableHead>
              <TableRow>
                <TableCell>Invoice</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Period</TableCell>
                <TableCell align="right">Amount (₹)</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.data.map((inv) => (
                <TableRow key={inv.id} hover sx={{ cursor: "pointer" }} onClick={() => setOpenId(inv.id)}>
                  <TableCell>{inv.invoicenumber || <em>Draft</em>}</TableCell>
                  <TableCell>{istDate(inv.createdat)}</TableCell>
                  <TableCell>
                    {istDate(inv.periodstart)} – {istDate(inv.periodend)}
                  </TableCell>
                  <TableCell align="right">{inr(inv.totalamount)}</TableCell>
                  <TableCell>
                    <StatusChip status={inv.syncstatus} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {openId && <InvoiceDialog ns={ns} invoiceId={openId} onClose={() => setOpenId(null)} />}
    </Box>
  );
}

function InvoiceDialog({ ns, invoiceId, onClose }: { ns: string; invoiceId: string; onClose: () => void }) {
  const identity = useIdentity();
  const [pdfError, setPdfError] = useState<Error | null>(null);
  const [downloading, setDownloading] = useState(false);

  const one = useQuery({
    queryKey: ["ankh-invoice", ns, invoiceId],
    queryFn: ({ signal }) => getAnkhInvoice(identity, ns, invoiceId, signal),
    retry: false,
  });

  async function downloadPdf(inv: AnkhInvoice) {
    setPdfError(null);
    setDownloading(true);
    try {
      saveBlob(await getAnkhInvoicePdf(identity, ns, inv.id), pdfFileName(inv));
    } catch (e) {
      setPdfError(e as Error);
    } finally {
      setDownloading(false);
    }
  }

  const inv = one.data;
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{inv ? `${inv.documentheading || "Invoice"} ${inv.invoicenumber ?? ""}` : "Invoice"}</DialogTitle>
      <DialogContent dividers>
        {one.isLoading && <Typography variant="body2">Loading…</Typography>}
        {one.isError && <ErrorAlert error={one.error as Error} what="this invoice" />}
        {pdfError && <ErrorAlert error={pdfError} what="the PDF" />}
        {inv && (
          <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="overline">From</Typography>
                <Typography variant="body2">{inv.supplierlegalname}</Typography>
                <Typography variant="body2" color="text.secondary">
                  GSTIN {inv.suppliergstin || "—"}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="overline">To</Typography>
                <Typography variant="body2">{inv.buyername}</Typography>
                <Typography variant="body2" color="text.secondary">
                  GSTIN {inv.buyergstin || "not registered"} · Place of supply {inv.placeofsupply}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="body2">
                  Issued {istDate(inv.createdat)} · Period {istDate(inv.periodstart)} – {istDate(inv.periodend)} ·{" "}
                  <StatusChip status={inv.syncstatus} />
                </Typography>
              </Grid>
            </Grid>

            <TableContainer>
              <Table size="small" aria-label="Invoice lines">
                <TableHead>
                  <TableRow>
                    <TableCell>Description</TableCell>
                    <TableCell>HSN/SAC</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Taxable (₹)</TableCell>
                    <TableCell align="right">GST</TableCell>
                    <TableCell align="right">Total (₹)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(inv.lineitems ?? []).map((l) => (
                    <TableRow key={l.id}>
                      <TableCell>{l.description}</TableCell>
                      <TableCell>{l.hsnsac ?? ""}</TableCell>
                      <TableCell align="right">{l.quantity}</TableCell>
                      <TableCell align="right">{inr(l.taxablevalue)}</TableCell>
                      <TableCell align="right">{l.gstpercent}%</TableCell>
                      <TableCell align="right">{inr(l.totalamount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ mt: 2, textAlign: "right" }}>
              <Typography variant="body2">Taxable value ₹{inr(inv.taxablevalue)}</Typography>
              {inv.totaligst > 0 ? (
                <Typography variant="body2">IGST ₹{inr(inv.totaligst)}</Typography>
              ) : (
                <Typography variant="body2">
                  CGST ₹{inr(inv.totalcgst)} · SGST ₹{inr(inv.totalsgst)}
                </Typography>
              )}
              <Typography variant="subtitle1">Total ₹{inr(inv.totalamount)}</Typography>
              <Typography variant="caption" color="text.secondary">
                {inv.totalamountinwords}
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" disabled={!inv || !inv.invoicenumber || downloading} onClick={() => inv && downloadPdf(inv)}>
          {downloading ? "Preparing…" : "Download PDF"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ReceiptList({ ns }: { ns: string }) {
  const identity = useIdentity();
  const [pdfError, setPdfError] = useState<Error | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ["ankh-receipts", ns],
    queryFn: ({ signal }) => listAnkhReceipts(identity, ns, signal),
    enabled: Boolean(identity.token && ns),
    retry: false,
  });

  async function download(rc: AnkhReceipt) {
    setPdfError(null);
    setBusy(rc.orderid);
    try {
      saveBlob(await getAnkhReceiptPdf(identity, ns, rc.orderid), pdfFileName({ invoicenumber: rc.receiptnumber, id: rc.orderid }));
    } catch (e) {
      setPdfError(e as Error);
    } finally {
      setBusy(null);
    }
  }

  return (
    <Box>
      {list.isLoading && <Typography variant="body2">Loading…</Typography>}
      {list.isError && <ErrorAlert error={list.error as Error} what="the receipt list" />}
      {pdfError && <ErrorAlert error={pdfError} what="the receipt PDF" />}
      {list.data && list.data.length === 0 && (
        <Alert severity="info">
          <AlertTitle>No receipts</AlertTitle>
          No payment to Ankh is recorded for <code>{ns}</code>.
        </Alert>
      )}
      {list.data && list.data.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small" aria-label="Receipts from Ankh">
            <TableHead>
              <TableRow>
                <TableCell>Receipt</TableCell>
                <TableCell>Received</TableCell>
                <TableCell>For invoice</TableCell>
                <TableCell>Method</TableCell>
                <TableCell align="right">Amount (₹)</TableCell>
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {list.data.map((rc) => (
                <TableRow key={rc.id}>
                  <TableCell>{rc.receiptnumber}</TableCell>
                  <TableCell>{istDate(rc.receivedat)}</TableCell>
                  <TableCell>{rc.invoicenumber || "advance"}</TableCell>
                  <TableCell>{rc.paymentmethod}</TableCell>
                  <TableCell align="right">{inr(rc.amount)}</TableCell>
                  <TableCell>
                    <Chip size="small" label={rc.status} color={rc.status === "Issued" ? "success" : "default"} variant="outlined" />
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" disabled={busy === rc.orderid} onClick={() => void download(rc)}>
                      {busy === rc.orderid ? "Preparing…" : "PDF"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
